/**
 * ConfirmationModal Component
 *
 * Reusable confirmation dialog for destructive or important actions.
 * Built on top of {@link Modal} — provides a standardized layout with
 * title, descriptive message, and confirm / cancel action buttons.
 *
 * @example
 * ```tsx
 * <ConfirmationModal
 *   open={showConfirm}
 *   title="Delete user"
 *   message="Are you sure you want to delete this user?"
 *   confirmLabel="Delete"
 *   cancelLabel="Cancel"
 *   variant="danger"
 *   onConfirm={handleDelete}
 *   onClose={() => setShowConfirm(false)}
 * />
 * ```
 */
import * as React from 'react';
import { cn, getTestId, type TestIdProps } from '../utils';
import { Modal } from './Modal';
import { Button, type ButtonProps } from './Button';

const VARIANT_BUTTON_MAP: Record<ConfirmationVariant, ButtonProps['variant']> = {
  danger: 'error',
  warning: 'primary',
  primary: 'primary'
};

type ConfirmationVariant = 'danger' | 'warning' | 'primary';

export interface ConfirmationModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Modal title */
  title: string;
  /** Descriptive message — can be a string or rich ReactNode (e.g. with bold interpolation) */
  message: React.ReactNode;
  /** Label for the confirm button */
  confirmLabel: string;
  /** Label for the cancel button */
  cancelLabel: string;
  /** Semantic variant controlling the confirm button style */
  variant?: ConfirmationVariant;
  /** Whether the confirm action is in progress */
  loading?: boolean;
  /** Whether the confirm button is disabled (independent of loading) */
  confirmDisabled?: boolean;
  /** Optional warning message displayed between header and footer */
  warning?: React.ReactNode;
  /** Extra content between the header and the warning — e.g. a confirmation input */
  body?: React.ReactNode;
  /** Called when the user confirms the action */
  onConfirm: () => void;
  /** Called when the modal is dismissed (cancel button or backdrop click) */
  onClose: () => void;
  /** Additional CSS classes for the modal wrapper */
  className?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  loading = false,
  confirmDisabled = false,
  warning,
  body,
  onConfirm,
  onClose,
  className,
  testId,
  id
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size='sm'
      className={cn('w-[90vw] sm:w-auto', className)}
      testId={getTestId({ testId, id })}
    >
      <Modal.Header onClose={onClose} label={title} description={message} />
      {body && <div className='px-lg pb-md'>{body}</div>}
      {warning && (
        <div className='px-lg pb-md'>
          <Modal.Warning>{warning}</Modal.Warning>
        </div>
      )}
      <Modal.Footer>
        <Modal.Action>
          <Button variant='default' onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={VARIANT_BUTTON_MAP[variant]}
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
};

ConfirmationModal.displayName = 'ConfirmationModal';
